import { useGoogleMapSearchContext } from "./components/GoogleMapSearchContext";
import GoogleMapSearch from "./GoogleMapSearch";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import { Box, Typography, Button, alpha, Divider } from "@mui/material";
import { OnBoardingStoreSelection } from "./OnBoardingContext";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";

const GOOGLE_MAPS_API_KEY = process.env
  .REACT_APP_GOFLYER_GOOGLE_MAP_API_KEY as string;

const ManualEntrySection = ({
  onManualEntry,
}: {
  onManualEntry: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Box display="flex" alignItems="center" gap={2} width="100%">
        <Divider sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t(messages.OR())}
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>

      <Button
        variant="outlined"
        size="large"
        onClick={onManualEntry}
        startIcon={<AddBusinessIcon />}
        sx={{
          minWidth: { xs: 200, sm: 240 },
          textTransform: "capitalize",
          height: 56,
          fontSize: "1.125rem",
          fontWeight: 600,
          borderRadius: 28,
          borderColor: "#02b7cf",
          color: "#02b7cf",
          "&:hover": {
            borderColor: "#0288a3",
            backgroundColor: alpha("#02b7cf", 0.05),
            transform: "translateY(-2px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        }}
      >
        {t(messages.Enter_Store_Details_Manually())}
      </Button>
    </>
  );
};

const ActionButtons = ({
  selectedStore,
  onContinue,
  onManualEntry,
  showManualEntry,
}: {
  selectedStore: OnBoardingStoreSelection | null;
  onContinue: () => void;
  onManualEntry: () => void;
  showManualEntry: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      justifyContent="center"
      flexDirection="column"
      alignItems="center"
      gap={2}
      mt={4}
      sx={{
        position: "sticky",
        bottom: 24,
        zIndex: 10,
      }}
    >
      <Button
        variant="contained"
        size="large"
        disabled={!selectedStore}
        onClick={onContinue}
        sx={{
          minWidth: { xs: 200, sm: 240 },
          textTransform: "capitalize",
          height: 56,
          fontSize: "1.125rem",
          fontWeight: 600,
          borderRadius: 28,
          backgroundColor: "#f58321",
          boxShadow: selectedStore
            ? "0 8px 24px rgba(245, 131, 33, 0.4)"
            : "0 2px 8px rgba(0,0,0,0.1)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: "#e57100",
            transform: "translateY(-2px)",
            boxShadow: "0 12px 32px rgba(245, 131, 33, 0.5)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
          "&:disabled": {
            backgroundColor: alpha("#f58321", 0.3),
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            transform: "none",
          },
        }}
      >
        {t(messages.Continue_with_Selected_Store())}
      </Button>

      {showManualEntry && <ManualEntrySection onManualEntry={onManualEntry} />}
    </Box>
  );
};

const GoogleMapSearchContent = ({
  selectedStore,
  onContinue,
  onManualEntry,
}: {
  selectedStore: OnBoardingStoreSelection | null;
  onContinue: () => void;
  onManualEntry: () => void;
}) => {
  const { results, googleResults, hasSearched, loading, googleLoading } =
    useGoogleMapSearchContext();

  const showManualEntry =
    hasSearched &&
    !loading &&
    !googleLoading &&
    results.length === 0 &&
    googleResults.length === 0;

  return (
    <ActionButtons
      selectedStore={selectedStore}
      onContinue={onContinue}
      onManualEntry={onManualEntry}
      showManualEntry={showManualEntry}
    />
  );
};

export const GoogleMapSearchWrapper = ({
  selectedStore,
  onContinue,
  onManualEntry,
}: {
  selectedStore: OnBoardingStoreSelection | null;
  onContinue: () => void;
  onManualEntry: () => void;
}) => {
  return (
    <GoogleMapSearch googleApiKey={GOOGLE_MAPS_API_KEY}>
      <GoogleMapSearchContent
        selectedStore={selectedStore}
        onContinue={onContinue}
        onManualEntry={onManualEntry}
      />
    </GoogleMapSearch>
  );
};
